// src/components/demo/DemoConfirm.js
//
// Check-out confirm ("Last look.") + success (design handoff §02-14/15).
// One summary card; the repeat-rider fast path shows the teal/lagoon
// "WAIVER ON FILE" panel here (design decision #3 — the <15s path is
// visually distinct so staff know they can skip ahead).
// Success = carbon ground, orange check block, "Board's yours, {name}."

import React, { useState } from 'react';
import { FScreen, FHeader, FHeadline, FBtn, FLabel, FBoardCard } from '../foam/FoamPrimitives';

const Row = ({ k, v, accent }) => (
  <div
    className="flex justify-between items-center"
    style={{ padding: '14px 0', borderBottom: '1px solid var(--foam-hair)' }}
  >
    <FLabel>{k}</FLabel>
    <span className="font-sans font-semibold text-[15px] text-right" style={{ color: accent || 'var(--foam-ink)' }}>
      {v}
    </span>
  </div>
);

// Check-out is a transaction now (demoService §4.2) — it can legitimately
// refuse: board already out on another phone, board not in today's fleet,
// no signal. Swallowing that left staff tapping a button that did nothing.
const FailPanel = ({ message }) => (
  <div style={{ border: '1.5px solid var(--foam-orange)', background: '#FFF7F3', padding: 16 }}>
    <div
      className="font-display font-extrabold uppercase text-[18px]"
      style={{ color: 'var(--foam-orange)', letterSpacing: '-0.01em' }}
    >
      Couldn&apos;t check out
    </div>
    <p className="font-sans text-[14px] mt-[6px]" style={{ color: 'var(--foam-ink)', lineHeight: 1.5 }}>
      {message}
    </p>
  </div>
);

export default function DemoConfirm({ product, rider, waiverOnFile, emailConsent, onConfirm, onBack, done }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (done) {
    return (
      <FScreen
        dark
        header={<FHeader dark right={<FLabel color="var(--foam-orange)">● Out on the water</FLabel>} />}
        footer={<FBtn primary height={72} className="!text-[18px]" onClick={onBack}>Scan next board</FBtn>}
      >
        <div className="flex-1 flex flex-col justify-center gap-[18px]">
          <div
            className="flex items-center justify-center"
            style={{ width: 56, height: 56, background: 'var(--foam-orange)', color: 'var(--foam-carbon)', fontSize: 30 }}
          >
            ✓
          </div>
          <FHeadline size={60} color="var(--foam-paper)">
            Board's<br />yours,<br /><FHeadline.Accent>{rider.firstName}.</FHeadline.Accent>
          </FHeadline>
          <p className="font-sans text-[16px]" style={{ color: '#C4C1B5', maxWidth: 300 }}>
            {product.name} is checked out. Have a rip — scan it again when it comes back in.
          </p>
        </div>
      </FScreen>
    );
  }

  return (
    <FScreen
      header={<FHeader right={<FLabel>Confirm</FLabel>} />}
      footer={
        <>
          <FBtn
            primary
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onConfirm();
              } catch (e) {
                setError(e?.message || 'The check-out did not go through. Check the signal and try again.');
                setBusy(false);
              }
            }}
          >
            {busy ? 'Checking out…' : waiverOnFile ? 'Check out — no signature' : 'Check out'}
          </FBtn>
          <FBtn ghost disabled={busy} onClick={onBack}>Back</FBtn>
        </>
      }
    >
      <FHeadline size={44}>Last look.</FHeadline>
      {error && <FailPanel message={error} />}
      <FBoardCard product={product} small />

      {waiverOnFile && (
        <div style={{ border: '1.5px solid var(--foam-teal)', background: 'var(--foam-lagoon, #CDE7E0)', padding: 18 }}>
          <div className="font-display font-extrabold uppercase text-[24px]" style={{ color: 'var(--foam-teal)', letterSpacing: '-0.01em' }}>
            ✓ Waiver on file
          </div>
          <p className="font-sans text-[14px] mt-[8px]" style={{ color: 'var(--foam-ink)' }}>
            <strong>{rider.firstName} {rider.lastName}</strong> already signed today. No second signature needed.
          </p>
        </div>
      )}

      <div>
        <Row k="Rider" v={`${rider.firstName} ${rider.lastName}`} />
        <Row k="Email" v={rider.email} />
        {rider.phone ? <Row k="Phone" v={rider.phone} /> : null}
        <Row k="Waiver" v={waiverOnFile ? '✓ On file' : '✓ Signed'} accent="var(--foam-teal)" />
        <Row
          k="Marketing"
          v={emailConsent ? 'Opted in' : 'Declined'}
          accent={emailConsent ? 'var(--foam-teal)' : 'var(--foam-ink-2)'}
        />
      </div>

      <p className="font-sans text-[12px]" style={{ color: 'var(--foam-ink-2)' }}>
        Staff: take the device back, give the details a last glance with {rider.firstName}, then hand over
        the board.
      </p>
    </FScreen>
  );
}
